import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import { Component, type ErrorInfo, lazy, type ReactNode, Suspense, useCallback, useEffect, useState } from 'react';
import { Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import LogoImageBlack from './assets/logo_text_black.svg';
import LogoImageWhite from './assets/logo_text_white.svg';
import AppLoadingOverlay from './components/AppLoadingOverlay/AppLoadingOverlay';
import { AppErrorScreen } from './components/ErrorFallback/ErrorFallback';
import HelpButton from './components/HelpButton/HelpButton';
import { hasActiveMaintenance, MaintenanceBanner } from './components/MaintenanceWarning/MaintenanceWarning';
import NavigationBar from './components/NavigationBar/NavigationBar';
import OrgUrlSync from './components/OrgUrlSync/OrgUrlSync';
import PlainChat from './components/PlainChat/PlainChat';
import { Toaster } from './components/ui/toaster';
import { WorkspaceDialogs } from './components/WorkspaceDialogs';
import { useDocumentTitle } from './lib/hooks/useDocumentTitle';
import { useOrgClaims } from './lib/hooks/useOrgClaims';
import { getStoredString, StorageKeys, setStoredValue } from './lib/persistence';
import { Tabs } from './lib/types/tabs';
import { ACTIVE_ORG_STORAGE_KEY, extractOrgId } from './lib/utils/orgRoutes';
import HomePage from './pages/HomePage';
import NotebookPage from './pages/NotebookPage';
import { useNavigation, useServiceTokens, useTheme, useWorkspace } from './providers';

// Code-split the heavy, route-gated surfaces so the initial bundle ships only
// the app shell + landing page. Each chunk loads on demand: when its route is
// visited, or when the workspace stream first mounts (which defers the ~760 KB
// @nvidia/ov-web-rtc WebRTC stack). recharts (scenarios/usage) and the docs
// renderer ride along into their owning route chunks. HomePage and NotebookPage
// stay eager: HomePage is the landing route, NotebookPage is mounted
// persistently across tab switches.
const StreamOnlyWindow = lazy(() => import('./components/StreamOnlyWindow/StreamOnlyWindow'));
const AssetList = lazy(() => import('./components/AssetList/AssetList'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const DocsPage = lazy(() => import('./pages/DocsPage'));
const JupyterRedirect = lazy(() => import('./pages/JupyterRedirect'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const OntologyPage = lazy(() => import('./pages/OntologyPage'));
const ScenariosPage = lazy(() => import('./pages/ScenariosPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const UsagePage = lazy(() => import('./pages/UsagePage'));

const NAVBAR_HEIGHT_PX = 44;

/** Lightweight fallback shown while a lazily-loaded route chunk resolves. */
function RouteFallback() {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 dark:border-gray-700 dark:border-t-gray-300" />
    </div>
  );
}

/**
 * Gate for the ``/:orgId/*`` route tree. The ``:orgId`` param matches any first
 * segment, so a legacy org-less path (``/scenarios``) would otherwise match
 * here with ``orgId="scenarios"``.
 *
 * Renders the fallback (not the routed page) until the active token's org
 * matches the URL org, so no page mounts and issues API requests under the
 * wrong org during the window where ``OrgUrlSync`` is still redirecting or
 * switching. Covers: a legacy / non-org segment, claims still loading, and a
 * pending cross-org switch.
 */
function OrgGuard() {
  const { orgId: urlOrg } = useParams();
  const { orgId: claimsOrgId } = useOrgClaims();
  if (!urlOrg?.startsWith('org_') || claimsOrgId !== urlOrg) {
    return <RouteFallback />;
  }
  return <Outlet />;
}

interface StreamWindowErrorBoundaryProps {
  children: ReactNode;
  resetKey: string;
  workspaceName?: string;
  onNavigateHome: () => void;
}

interface StreamWindowErrorBoundaryState {
  hasError: boolean;
}

class StreamWindowErrorBoundary extends Component<StreamWindowErrorBoundaryProps, StreamWindowErrorBoundaryState> {
  public state: StreamWindowErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): StreamWindowErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[workspace] StreamOnlyWindow crashed', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  componentDidUpdate(prevProps: StreamWindowErrorBoundaryProps) {
    if (this.props.resetKey !== prevProps.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="absolute inset-0">
          <AppErrorScreen
            title="Workspace view failed"
            description={
              this.props.workspaceName
                ? `The "${this.props.workspaceName}" workspace view hit a UI error. Your workspace is still running.`
                : 'The workspace view hit a UI error. Your workspace is still running.'
            }
            actionLabel="Back to home"
            onAction={this.props.onNavigateHome}
          />
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Main application content component.
 * Uses contexts for navigation and workspace-related functionality.
 */
function AppContent() {
  const { resolvedTheme } = useTheme();
  const { currentTab, switchTab, hasOpenedWorkspace } = useNavigation();
  const { tokens: serviceTokens } = useServiceTokens();
  const { user, logout } = useAuth0();
  const { orgId, orgName, userId, isLoading: orgClaimsLoading } = useOrgClaims();
  const location = useLocation();

  // Whether the active token's org matches the org in the URL. The persistent
  // workspace/notebook panes render outside the guarded <Routes> subtree, keyed
  // only on currentTab, so they must respect this too -- otherwise following a
  // cross-org link like /org_B/workspace would surface the old org's stream
  // under the target URL until OrgUrlSync finishes switching or redirecting.
  const orgSettled = extractOrgId(location.pathname) === orgId;

  // Get workspace state and actions from context
  const { connectedWorkspace, streamTarget, isRestoringWorkspace, workspaces } = useWorkspace();

  // The connected-workspace snapshot is minimal; read the live record from the
  // polled list so the maintenance auto-pause time stays current.
  const connectedLive = connectedWorkspace ? workspaces.find((w) => w.id === connectedWorkspace.id) : undefined;

  // Dismissible maintenance banner. Keyed by workspace + shutdown time so a
  // rescheduled auto-pause re-shows it; persisted so it stays dismissed across
  // the reload an org switch triggers.
  const maintenanceKey = connectedLive?.scheduledShutdownAt
    ? `${connectedLive.id}:${connectedLive.scheduledShutdownAt}`
    : null;
  const [dismissedMaintenanceKey, setDismissedMaintenanceKey] = useState<string | null>(() =>
    getStoredString(StorageKeys.DISMISSED_MAINTENANCE)
  );
  const dismissMaintenance = useCallback(() => {
    if (!maintenanceKey) return;
    setStoredValue(StorageKeys.DISMISSED_MAINTENANCE, maintenanceKey);
    setDismissedMaintenanceKey(maintenanceKey);
  }, [maintenanceKey]);
  const showMaintenanceBanner =
    currentTab === Tabs.WORKSPACE &&
    orgSettled &&
    connectedLive != null &&
    hasActiveMaintenance(connectedLive) &&
    maintenanceKey !== dismissedMaintenanceKey;

  // Update browser tab title based on current page
  useDocumentTitle(currentTab);

  // Set Sentry user and organization context for error attribution
  useEffect(() => {
    Sentry.setUser(user?.email ? { email: user.email, id: userId ?? undefined } : null);
    if (orgId) Sentry.setTag('org_id', orgId);
    if (orgName) Sentry.setTag('org_name', orgName);
  }, [user?.email, userId, orgId, orgName]);

  const [focusMode, setFocusMode] = useState(false);
  const toggleFocusMode = useCallback(() => setFocusMode((prev) => !prev), []);

  // Authenticated, but claims finished loading with no org (missing org_id
  // claim, or a claims-fetch error that won't auto-retry). Surface it rather
  // than leave every route stuck on an indefinite OrgGuard spinner with no
  // diagnostic.
  if (!orgClaimsLoading && !orgId) {
    return (
      <div className="h-screen w-screen">
        <AppErrorScreen
          title="No organization access"
          description="We couldn't load an organization for your account. Try signing in again, or contact your administrator if this keeps happening."
          actionLabel="Sign out"
          onAction={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        />
      </div>
    );
  }

  return (
    <div
      className="transition-colors duration-200"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: resolvedTheme === 'dark' ? '#0a0f1a' : '#fafafa',
      }}
    >
      <OrgUrlSync />
      <Toaster />
      <PlainChat />
      <HelpButton />

      {/* Workspace confirmation dialogs */}
      <WorkspaceDialogs />

      {/* Header -- inert in focus mode to prevent keyboard focus on hidden nav */}
      <div inert={focusMode || undefined}>
        <NavigationBar
          logoSrc={resolvedTheme === 'dark' ? LogoImageWhite : LogoImageBlack}
          currentTab={currentTab}
          onTabChange={switchTab}
        />
      </div>

      {/* Scheduled node-maintenance banner for the connected workspace. Rendered
          as a top-level fixed notification just below the nav bar -- not inside
          the stream pane -- so it clears the pane's overflow clip and the stream's
          floating windows (z-index 101-107) instead of being buried under them.
          pointer-events-none wrapper keeps the rest of the viewport interactive. */}
      {showMaintenanceBanner && connectedLive ? (
        <div
          className="pointer-events-none fixed inset-x-0 flex justify-center px-4"
          style={{ top: focusMode ? 12 : NAVBAR_HEIGHT_PX + 8, zIndex: 1050 }}
        >
          <MaintenanceBanner
            workspaces={[
              {
                id: connectedLive.id,
                name: connectedLive.name,
                scheduledShutdownAt: connectedLive.scheduledShutdownAt as number,
              },
            ]}
            className="pointer-events-auto max-w-[640px] shadow-md backdrop-blur-md"
            onDismiss={dismissMaintenance}
          />
        </div>
      ) : null}

      {/* Stream mounted once WORKSPACE tab has been opened, then kept in background */}
      {hasOpenedWorkspace && (
        <div
          style={{
            position: 'fixed',
            top: focusMode ? 0 : NAVBAR_HEIGHT_PX,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            height: focusMode ? '100%' : `calc(100% - ${NAVBAR_HEIGHT_PX}px)`,
            display: currentTab === Tabs.WORKSPACE && orgSettled ? 'block' : 'none',
            backgroundColor: resolvedTheme === 'dark' ? '#030712' : '#e5e7eb',
            zIndex: focusMode ? 1001 : undefined,
            transition: 'top 0.2s ease-out, height 0.2s ease-out',
          }}
        >
          <StreamWindowErrorBoundary
            resetKey={streamTarget?.signaling.host || 'none'}
            workspaceName={connectedWorkspace?.name}
            onNavigateHome={() => switchTab(Tabs.HOME)}
          >
            <Suspense fallback={<AppLoadingOverlay />}>
              <StreamOnlyWindow
                key={streamTarget?.signaling.host || 'none'}
                target={streamTarget}
                isRestoringWorkspace={isRestoringWorkspace}
                onNavigateHome={() => switchTab(Tabs.HOME)}
                workspaceName={connectedWorkspace?.name}
                workspaceId={connectedWorkspace?.id}
                workspaceVersion={connectedWorkspace?.version}
                focusMode={focusMode}
                onToggleFocusMode={toggleFocusMode}
              />
            </Suspense>
          </StreamWindowErrorBoundary>
        </div>
      )}

      {/* Notebook mounted persistently, hidden when not active */}
      <div
        style={{
          position: 'fixed',
          top: NAVBAR_HEIGHT_PX,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          height: `calc(100% - ${NAVBAR_HEIGHT_PX}px)`,
          display: currentTab === Tabs.NOTEBOOK && orgSettled ? 'block' : 'none',
          backgroundColor: resolvedTheme === 'dark' ? '#030712' : '#e5e7eb',
        }}
      >
        <NotebookPage />
      </div>

      <div
        className="bg-gray-50 transition-colors duration-200 dark:bg-[#0a0f1a]"
        style={{
          display: orgSettled && (currentTab === Tabs.WORKSPACE || currentTab === Tabs.NOTEBOOK) ? 'none' : 'block',
          position: 'fixed',
          top: NAVBAR_HEIGHT_PX,
          left: 0,
          right: 0,
          bottom: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          // Reserve the scrollbar gutter so toggling content that overflows
          // (e.g. demo-mode filters in Settings) doesn't shift the page sideways
          // when the scrollbar appears. Applies to every routed page since they
          // all scroll through this single container.
          scrollbarGutter: 'stable',
        }}
      >
        <Suspense fallback={<RouteFallback />}>
          {/* Every in-app route is org-scoped under /:orgId. OrgUrlSync (mounted
              above) redirects org-less / bare-org paths and auto-switches when
              the path points at a different org. */}
          <Routes>
            <Route path="/:orgId" element={<OrgGuard />}>
              {/* Bare org path -- OrgUrlSync redirects it to home. */}
              <Route index element={<RouteFallback />} />
              <Route path="home" element={<HomePage />} />
              <Route path="workspace" element={null} />
              <Route path="notebook" element={null} />
              <Route
                path="jupyter"
                element={
                  <JupyterRedirect
                    workspaceUrl={streamTarget?.signaling.host || null}
                    workspaceToken={serviceTokens?.workspace_token}
                  />
                }
              />
              <Route path="assets" element={<AssetList />} />
              <Route path="scenarios" element={<ScenariosPage />} />
              <Route path="usage" element={<UsagePage />} />
              <Route path="docs/*" element={<DocsPage />} />
              <Route path="admin" element={<AdminPage />} />
              <Route path="ontology" element={<OntologyPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
            {/* Org-less paths render the fallback while OrgUrlSync redirects. */}
            <Route path="*" element={<RouteFallback />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}

/**
 * Main App component with authentication handling.
 * All providers are wrapped in AppProviders (main.tsx), so this component
 * only handles auth state and renders content.
 */
function App() {
  const { isLoading, isAuthenticated, error, loginWithRedirect: login, logout } = useAuth0();

  // Automatically redirect to Auth0 login when not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      // Check for organization invitation params in URL (from Auth0 invitation redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const organization = urlParams.get('organization');
      const invitation = urlParams.get('invitation');

      if (organization && invitation) {
        // User clicked an invitation link - pass org context to Auth0
        login({
          authorizationParams: {
            organization,
            invitation,
          },
        });
      } else {
        // Normal login flow
        login();
      }
    }
  }, [isLoading, isAuthenticated, error, login]);

  if (isLoading) {
    return <AppLoadingOverlay />;
  }

  // Show error state if authentication fails
  if (!isAuthenticated && error) {
    // Determine error type and message
    const errorMessage = error.message || '';
    const isEmailVerificationError = errorMessage.toLowerCase().includes('verify your email');
    const isOrganizationError = errorMessage.toLowerCase().includes('organization');

    let title = 'Unable to sign in';
    let description = 'Something went wrong during authentication. Please try again.';

    if (isEmailVerificationError) {
      title = 'Please verify your email';
      description = 'Check your inbox for a verification email from Antioch, then try signing in again.';
    } else if (isOrganizationError) {
      title = 'No organization access';
      description = "Your account isn't associated with an organization. Please contact your administrator for access.";
    }

    return (
      <div className="h-screen w-screen">
        <AppErrorScreen
          title={title}
          description={description}
          actionLabel="Try again"
          onAction={() => {
            // Clear any pinned org before retrying. If the stored org became
            // invalid (user removed from it, org deleted, or a same-origin dev
            // env pointing at a different Auth0 tenant), it would make every
            // login re-pin the same rejected org and loop on this error. Reload
            // so AuthProvider re-bootstraps org-less and the normal flow can
            // resolve a valid org (ENG-1217 review).
            try {
              window.localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
            } catch {
              // localStorage unavailable -- nothing to clear.
            }
            window.location.assign(window.location.origin);
          }}
          secondaryActionLabel="Sign out"
          onSecondaryAction={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        />
      </div>
    );
  }

  // Show loading while redirecting to Auth0
  if (!isAuthenticated) {
    return <AppLoadingOverlay />;
  }

  // User is authenticated, show the app content
  return <AppContent />;
}

export default App;
